import React from 'react';
import { SupportedLanguage } from '../../types/language';
import { useTranslation } from 'react-i18next';
import { 
  ShieldCheckIcon, 
  ExclamationTriangleIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon
} from '@heroicons/react/24/outline';

interface RiskOverviewCardProps {
  currentRisk: {
    score: number | null;
    level: string | null;
    hasLiveData?: boolean;
    change24h: number;
  };
  todayArticleCount: number;
  changeLabel?: string;
  language: SupportedLanguage;
}

export const RiskOverviewCard: React.FC<RiskOverviewCardProps> = ({
  currentRisk,
  todayArticleCount,
  changeLabel,
  language,
}) => {
  const { t } = useTranslation();
  const getRiskMessage = (level: string, score: number) => {
    const c = t('public.riskCopy', { returnObjects: true }) as any;
    const messages: Record<string, {
      title: string;
      message: string;
      icon: any;
      color: string;
      borderColor: string;
    }> = {
      'MINIMAL': {
        title: c.minimal.title,
        message: c.minimal.message,
        icon: ShieldCheckIcon,
        color: "text-green-600 bg-green-50",
        borderColor: "border-green-200"
      },
      'LOW': {
        title: c.low.title,
        message: c.low.message,
        icon: ShieldCheckIcon,
        color: "text-green-600 bg-green-50",
        borderColor: "border-green-200"
      },
      'MEDIUM': {
        title: c.medium.title,
        message: c.medium.message,
        icon: ExclamationTriangleIcon,
        color: "text-yellow-600 bg-yellow-50",
        borderColor: "border-yellow-200"
      },
      'HIGH': {
        title: c.high.title,
        message: c.high.message,
        icon: ExclamationTriangleIcon,
        color: "text-orange-600 bg-orange-50",
        borderColor: "border-orange-200"
      },
      'CRITICAL': {
        title: c.critical.title,
        message: c.critical.message,
        icon: ExclamationTriangleIcon,
        color: "text-red-600 bg-red-50",
        borderColor: "border-red-200"
      }
    };

    return messages[level] || messages['LOW'];
  };

  const noLiveData = currentRisk.hasLiveData === false || currentRisk.score == null || !currentRisk.level;
  // No live analyses in range: neutral "no recent analyses" state, never a Safe/Minimal look
  const riskInfo = noLiveData
    ? {
        title: t('public.timeline.noRecentAnalyses'),
        message: '',
        icon: ChartBarIcon,
        color: 'text-gray-500 bg-gray-50',
        borderColor: 'border-gray-200',
      }
    : getRiskMessage(currentRisk.level as string, currentRisk.score as number);
  const Icon = riskInfo.icon;

  return (
    <div className={`bg-white rounded-2xl shadow-lg p-3 mb-4 border-2 ${riskInfo.borderColor}`}>
      <div className="text-sm divide-y divide-gray-100">
        {/* Row 1: Current risk level with score */}
        <div className="py-1.5 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0 text-xs sm:text-sm text-gray-600">
            <div className={`p-1.5 rounded-md ${riskInfo.color}`}>
              <Icon className="h-4 w-4" />
            </div>
            <span className="min-w-0">{t('public.metrics.currentRiskLevel')}</span>
          </div>
          <div className="text-right text-sm sm:text-base font-semibold text-gray-900">
            <span className="sm:whitespace-nowrap">{riskInfo.title}</span>
            {!noLiveData && (
              <>
                <span className="mx-1 text-gray-300">•</span>
                <span>{currentRisk.score}</span>
                <span className="text-gray-500">/10</span>
              </>
            )}
          </div>
        </div>

        {/* Row 2: Change over period in words */}
        <div className="py-1.5 flex items-start justify-between gap-3">
          <span className="text-xs sm:text-sm text-gray-600">{changeLabel || t('public.metrics.change24h')}</span>
          <div className="flex items-center gap-1 text-right">
            {currentRisk.change24h > 0.1 ? (
              <>
                <ArrowTrendingUpIcon className="h-4 w-4 text-red-500" />
                <span className="font-medium text-red-600">+{currentRisk.change24h.toFixed(1)}</span>
              </>
            ) : currentRisk.change24h < -0.1 ? (
              <>
                <ArrowTrendingDownIcon className="h-4 w-4 text-green-500" />
                <span className="font-medium text-green-600">{currentRisk.change24h.toFixed(1)}</span>
              </>
            ) : (
              <>
                <MinusIcon className="h-4 w-4 text-gray-400" />
                <span className="font-medium text-gray-600">{t('public.metrics.stable')}</span>
              </>
            )}
          </div>
        </div>

        {/* Row 3: Articles today with text */}
        <div className="py-1.5 flex items-start justify-between gap-3">
          <span className="text-xs sm:text-sm text-gray-600">{t('public.timeline.articlesAnalyzed')}</span>
          <div className="flex items-center gap-1 text-right">
            <ChartBarIcon className="h-4 w-4 text-blue-500" />
            <span className="font-medium text-gray-900">{todayArticleCount}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
